import { createRoot } from "react-dom/client"
import * as Sentry from "@sentry/react"
import { loadConfig, ConfigLoadError } from "./shared/config"
import { initGoogleTagManager } from "./shared/lib/analytics"
import { warmApiUrlCache } from "./shared/api/client"
import { ConfigError } from "./components/ConfigError"
import App from "./app/App"
import "./index.css"

// Initialize Telegram WebApp SDK (applies safe area insets)
import "./shared/lib/telegram"

// Hot endpoints whose URLs are pre-built after config loads so the first
// bet/trick request pays no URL-construction cost.
const HOT_ENDPOINTS = [
  "/v1/games/pvp-roulette/bet",
  "/v1/games/pvp-roulette/trick",
  "/v1/games/pvp-roulette/room",
  "/v1/user/balance",
]

if ("serviceWorker" in navigator) {
  navigator.serviceWorker.register("/sw.js")
}

const root = createRoot(document.getElementById("root")!)

async function bootstrap() {
  try {
    const cfg = await loadConfig()

    if (cfg.sentryDsn) {
      Sentry.init({
        dsn: cfg.sentryDsn,
        environment: cfg.env,
        release: import.meta.env.VITE_SENTRY_RELEASE,
      })
    }

    if (cfg.env === "prod") {
      initGoogleTagManager()
    }

    warmApiUrlCache(HOT_ENDPOINTS)

    // Inject <link rel="preconnect"> for the API origin so the browser
    // pre-establishes the TCP+TLS connection before any JS request fires.
    // This eliminates the idle-reconnect 300-400ms penalty on cold start.
    try {
      const apiOrigin = new URL(
        cfg.apiBaseUrl.startsWith("/") ? window.location.href : cfg.apiBaseUrl
      ).origin
      if (apiOrigin !== window.location.origin) {
        const link = document.createElement("link")
        link.rel = "preconnect"
        link.href = apiOrigin
        link.crossOrigin = "anonymous" // needed for TLS pre-handshake with CORS
        document.head.appendChild(link)
      }
    } catch {
      // Non-critical — requests work without it.
    }

    root.render(<App />)
  } catch (error) {
    console.error("[Bootstrap] Failed to load config:", error)
    root.render(
      <ConfigError
        error={
          error instanceof ConfigLoadError
            ? error
            : new Error("Failed to initialize app")
        }
      />
    )
  }
}

bootstrap()
